<template>
  <div class="wizard-step-content wizard-step-export">
    <ul class="u-reset wizard-step-content__group-list">
      <li v-if="mapNumber" class="wizard-step-content__group">
        <b>{{ $t('wizard_step.export.congratulations_title') }}</b>
        <br />
        {{ $t('wizard_step.export.congratulations_text', { number: mapNumber }) }}
        <br />
        {{ $t('wizard_step.export.congratulations_cta') }}
        <a href="https://maps.worldmapgenerator.com/list" rel="noopener noreferrer" target="_blank">
          {{ $t('wizard_step.export.congratulations_gallery') }}
        </a>
      </li>
      <li class="wizard-step-content__group">
        <radio-list
          :items="$options.exportFormatList"
          :title="$t('wizard_step.export.format_select_title')"
          :value.sync="activeExportFormatId"
        />
      </li>
      <li class="wizard-step-content__group">
        <ul class="u-reset wizard-step-export__size-list">
          <li class="wizard-step-export__size">
            <ui-model-provider v-slot="{ value, updateValue }" :value.sync="width">
              <ui-input
                :value="value"
                required
                type="number"
                v-bind="thresholdWidths"
                @input="updateValue"
              >
                <template #label>
                  <span>{{ $t('wizard_step.export.width_input_label') }}</span>
                </template>
              </ui-input>
            </ui-model-provider>
          </li>
          <li class="wizard-step-export__size">
            <ui-model-provider v-slot="{ value, updateValue }" :value.sync="height">
              <ui-input
                :value="value"
                required
                type="number"
                v-bind="thresholdHeights"
                @input="updateValue"
              >
                <template #label>
                  <span>{{ $t('wizard_step.export.height_input_label') }}</span>
                </template>
              </ui-input>
            </ui-model-provider>
          </li>
        </ul>
      </li>
      <li class="wizard-step-content__group">
        <h2 class="u-reset wizard-step-content__sub-title">
          {{ $t('wizard_step.export.size_select_title') }}
        </h2>
        <ul class="u-reset wizard-step-export__btn-list">
          <li
            v-for="exportSize in $options.exportSizeList"
            :key="exportSize.id"
            class="wizard-step-export__btn-item"
          >
            <button
              :class="getButtonClasses(exportSize.width, exportSize.height)"
              class="btn btn--primary btn--small wizard-step-export__size-btn"
              @click="setSize(exportSize.width, exportSize.height)"
            >
              {{ $t(exportSize.label) }}
            </button>
          </li>
        </ul>
      </li>
      <li class="wizard-step-content__group">
        <h2 class="u-reset wizard-step-content__sub-title">
          {{ $t('wizard_step.export.download_title') }}
        </h2>

        <ul class="u-reset wizard-step-export__btn-list wizard-step-export__btn-list--column">
          <li class="wizard-step-export__btn-item">
            <export-link
              :disabled="!isReady"
              :download="fileName"
              :href="downloadDataURL"
              img="/images/social_media_icons/download_base.png"
              @click.native="onDownloadRequested"
            >
              {{ $t('wizard_step.export.export_button_label') }}
            </export-link>
          </li>
        </ul>
      </li>
      <li class="wizard-step-content__group">
        <h2 class="u-reset wizard-step-content__sub-title">
          {{ $t('wizard_step.export.share_title') }}
        </h2>

        <ul class="u-reset wizard-step-export__btn-list wizard-step-export__btn-list--column">
          <li class="wizard-step-export__btn-item">
            <export-link
              :disabled="!isReady"
              :href="emailHref"
              img="/images/social_media_icons/email_base.png"
              size="small"
              @click.native="onDownloadRequested"
            >
              {{ $t('wizard_step.export.email_button_label') }}
            </export-link>
          </li>
          <li class="wizard-step-export__btn-item">
            <export-link
              :disabled="!isReady"
              :href="facebookHref"
              img="/images/social_media_icons/facebook_base.png"
              size="small"
              @click.native="onDownloadRequested"
            >
              {{ $t('wizard_step.export.facebook_button_label') }}
            </export-link>
          </li>
          <li class="wizard-step-export__btn-item">
            <export-link
              :disabled="!isReady"
              :href="twitterHref"
              img="/images/social_media_icons/twitter_base.png"
              size="small"
              @click.native="onDownloadRequested"
            >
              {{ $t('wizard_step.export.twitter_button_label') }}
            </export-link>
          </li>
        </ul>
      </li>
    </ul>

    <div class="wizard-step-export__exporter">
      <world-map
        :facets="[]"
        :has-watermark="true"
        :map-padding="[4, 4, 4, 4]"
        :rendering-adapter-id="activeExportFormat.renderingAdapterId"
        :target-height="height"
        :target-width="width"
        class="wizard-step-export__renderer"
        v-bind="mapConfig"
        @map-drawn="onDownloadMapDrawn"
      />
      <world-map
        :facets="[]"
        :has-watermark="false"
        :map-padding="[4, 4, 4, 4]"
        :rendering-adapter-id="previewExportFormat.renderingAdapterId"
        :target-height="261"
        :target-width="500"
        class="wizard-step-export__renderer"
        v-bind="mapConfig"
        @map-drawn="onPreviewMapDrawn"
      />
    </div>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import { debounce } from 'lodash-es'
  import { reduceRotationPrecision, reduceZoomPrecision } from '../../store/world-map/util'
  import { getImage, putImage } from './util'
  import RadioList from '@/components/radio-list/radio-list'
  import {
    exportFormatIds,
    exportFormatList,
    exportSizeList,
    renderingAdapterIds,
  } from '@/assets/js/model/export'
  import WorldMap from '@/components/world-map/world-map'
  import UiInput from '@/components/form/ui-input/ui-input'
  import UiModelProvider from '@/components/form/ui-model-provider/ui-model-provider'
  import { overlayInstanceIds } from '@/assets/js/model/overlay-instance'
  import bemMixin from '@/assets/js/mixin/bem'
  import ExportLink from '@/components/export-link/export-link'
  import { BASE_URL } from '@/assets/js/util/nuxt/seo'
  import { createImageHash } from '@/assets/js/util/misc/map-config'
  import { pack } from '@/assets/js/util/misc/json-data'

  export default {
    components: { ExportLink, UiModelProvider, UiInput, WorldMap, RadioList },
    mixins: [bemMixin('wizard-step-export')],
    props: {
      mapConfig: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        width: 1920,
        height: 1080,
        downloadDataURL: null,
        previewDataURL: null,
        previewImageURL: null,
        previewHash: null,
        mapNumber: null,
        activeExportFormatId: exportFormatIds.PNG,
      }
    },
    computed: {
      ...mapState('world-map', ['renderingAdapterData']),
      isReady() {
        return !!this.downloadDataURL && !!this.previewImageURL
      },
      activeExportFormat() {
        return exportFormatList.find(({ id }) => id === this.activeExportFormatId)
      },
      previewExportFormat() {
        return exportFormatList.find(({ id }) => id === exportFormatIds.PNG)
      },
      fileName() {
        return `generated_with_worldmapgenerator.${this.activeExportFormat.fileExtension}`
      },
      thresholdWidths() {
        return this.getThresholdValues(this.$options.exportSizeList.map(({ width }) => width))
      },
      thresholdHeights() {
        return this.getThresholdValues(this.$options.exportSizeList.map(({ height }) => height))
      },
      shareData() {
        return {
          url: `https://maps.worldmapgenerator.com/share/?h=${this.previewHash}`,
          baseurl: BASE_URL,
          hashtags: ['worldmapgenerator', 'worldmap', 'maps'],
          via: 'JuliamiaGrafik',
        }
      },
      emailHref() {
        const subject = this.$t('wizard_step.export.email_subject', this.shareData)
        const body = this.$t('wizard_step.export.email_body', this.shareData)

        return `mailto:?subject=${subject}&body=${body}`
      },
      facebookHref() {
        const { url } = this.shareData
        const quote = this.$t('wizard_step.export.facebook_quote', this.shareData)
        const query = this.createUrlSearchParams({ quote, u: url })
        return `https://www.facebook.com/share.php?${query}`
      },
      twitterHref() {
        const { hashtags, url, via } = this.shareData
        const text = this.$t('wizard_step.export.twitter_text', this.shareData)
        const query = this.createUrlSearchParams({ text, hashtags, url, via })
        return `https://twitter.com/intent/tweet?${query}`
      },
    },
    watch: {
      previewDataURL: {
        handler() {
          this.savePreview()
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions('overlay', ['closeOverlay', 'openOverlay']),
      ...mapActions('world-map', ['loadRenderingAdapter']),
      onDownloadMapDrawn({ adapter, element }) {
        this.downloadDataURL = adapter.createDataURL(element, this.activeExportFormat.fileType)
      },
      onPreviewMapDrawn({ adapter, element }) {
        this.previewDataURL = adapter.createDataURL(element, this.previewExportFormat.fileType)
      },
      onDownloadRequested() {
        this.openOverlay({
          id: overlayInstanceIds.DONATION_CONTENT,
          transition: 'trs-simple-fade',
          disableScroll: false,
          facets: ['donation'],
          component: 'overlay-donation-content',
          props: {},
        })
      },
      getButtonClasses(width, height) {
        const isActive = this.width === width && this.height === height

        return [this.bemAdd(isActive ? 'is-active' : null, 'size-btn')]
      },
      getThresholdValues(values) {
        return {
          min: Math.min(...values),
          max: Math.max(...values),
        }
      },
      setSize(width, height) {
        this.width = width
        this.height = height
      },
      createUrlSearchParams(params) {
        return new URLSearchParams(params).toString()
      },
      savePreview: debounce(async function () {
        if (!this.previewDataURL) {
          return
        }

        const sanitizedConfig = {
          ...this.mapConfig,
          rotation: reduceRotationPrecision(this.mapConfig.rotation),
          zoom: reduceZoomPrecision(this.mapConfig.zoom),
          renderingAdapterId: renderingAdapterIds.CANVAS,
          wizardStepProgressIdx: Number.MAX_SAFE_INTEGER,
        }
        const hash = await createImageHash(sanitizedConfig)
        const getResponse = await getImage({ hash })

        // This is for localhost usage so that maps are not saved online when testing locally
        if (!this.$config.allowMapPreviewSave) {
          const previewHash = '79e2749a7a60e071e8dac8a18cd06ccd7d7dfcfc'
          this.previewImageURL = `https://maps.worldmapgenerator.com/images/1.0.0/79/${previewHash}.png`
          this.previewHash = previewHash
          this.mapNumber = 666
        } else if (getResponse.data.statusCode === 404) {
          const file = this.previewDataURL
          const config = pack(sanitizedConfig)
          const putResponse = await putImage({ config, hash, file })
          this.previewImageURL = putResponse.data.payload.path
          this.previewHash = hash
          this.mapNumber = putResponse.data.payload.count
        } else if (getResponse.data.statusCode === 200) {
          this.previewImageURL = getResponse.data.payload.path
          this.previewHash = hash
        }
      }, 200),
    },
    exportFormatList,
    exportSizeList,
  }
</script>

<style lang="scss" src="./_wizard-step-export.scss" />
